import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Collapse,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { RadioIcon } from "lucide-react";

import { Alert, Autocomplete, Button, Modal } from "@repo/ui";
import { type CompanyType } from "@repo/types";
import { listedCompanyType } from "utils/companyLabel";
import {
  CompanyAvatar,
  EncodeSessionBody,
  PACKAGE_HANDLE_URL_RE,
  PackagePreview,
  Preview,
  StatusChip,
} from "components";
import type { EncodeSessionTarget } from "components";
import { COMPANY_SEGMENT_STATUSES } from "constants/statuses";
import { useEncodingDevice, useSnackbar } from "contexts";
import { useCanAccess, useEncodeSession } from "hooks";
import type { EncodeSessionTargetRef } from "hooks";
import { useCompaniesQuery } from "hooks/api";
import { api } from "services";
import { getErrorMessage } from "utils";
import { listedCompanies } from "utils/listedCompany";

interface EncodeTagsModalProps {
  /**
   * Pre-resolved fill target (Packages page kebab) — the modal is just the session (fill that box).
   * Omit it (Tags page) to show the optional company picker above the session in the same view.
   */
  target?: EncodeSessionTarget | null;
  open: boolean;
  onClose: () => void;
  refetch?: () => void;
}

interface CompanyOption {
  label: string;
  value: string;
  image?: string | null;
  /** VRG-808 — a `kind = 'demo'` acquisition company. Badged in the list; see the fetch below. */
  type?: CompanyType | null;
}

/**
 * VERA-790 — the "Add tags" encode session, as ONE MODAL.
 *  - Packages page: a `target` prop is passed → the modal is just the session (fill that box).
 *  - Tags page: no target → the session arms immediately and encodes tags into Vera's UNASSIGNED
 *    pool by default. A company picker sits above it (OPTIONAL): pick one and the tags are assigned
 *    to that company instead; leave it blank and they stay Vera-owned, assignable to a package
 *    later via the "Add to package" step. Tag type is not asked — the chip only reports its family
 *    (424), not a SKU, so it defaults to NTAG 424 DNA.
 *
 * Tap NTAG 424 DNA tags one at a time on the reader; each is encoded (SUN NDEF; optionally locked)
 * and provisioned. The reader-connect surface + running list come from the shared `EncodeSessionBody`;
 * the loop/provision machinery from `useEncodeSession` (also driving `BulkEncodeTagsModal`).
 */
export const EncodeTagsModal = (props: EncodeTagsModalProps) => {
  const { target, open, onClose, refetch } = props;
  const theme = useTheme();
  const { isConnected, readerName, helperReachable } = useEncodingDevice();
  const { open: notification } = useSnackbar();
  const { isHasAccess: canEncode } = useCanAccess({
    action: "internal_package_upload",
  });

  // No target prop → the Tags-page flow, which shows the optional company picker above the session.
  const isConfigureFlow = !target;

  // The company the session's tags were assigned to via the POST-scan "Assign to company" step
  // (null until assigned).
  const [companyAssigned, setCompanyAssigned] = useState<{
    id: string;
    name: string;
    logo?: string | null;
  } | null>(null);
  const [assigningCompany, setAssigningCompany] = useState(false);
  // The package the session's tags were assigned to via the post-scan "Add to package" tap (the
  // full pkg object, incl. tag_shop_product for the product preview). Null until a box is tapped.
  const [packageAssigned, setPackageAssigned] = useState<any | null>(null);
  // VERA-790 confirm-to-move: of the session's tags, which are ALREADY assigned to a DIFFERENT
  // company/package than the pending target (so Done will MOVE them). {uid, name} of the current owner.
  const [companyConflicts, setCompanyConflicts] = useState<
    { uid: string; name: string }[]
  >([]);
  const [packageConflicts, setPackageConflicts] = useState<
    { uid: string; name: string }[]
  >([]);
  // Lock is chosen BEFORE the session starts and fixed for its duration (default OFF = testing).
  const [lock, setLock] = useState(false);
  // VRG-836 — write protection (rotate app key 0 + gate the chip's NDEF write/change rights
  // behind it). A SEPARATE opt-in from `lock`, defaulting OFF, so turning on today's lock can
  // never start rotating key 0. Reset with the session, like `lock`.
  const [writeProtect, setWriteProtect] = useState(false);

  // Tap-box-to-assign: the encode loop calls this (via a ref wrapper so it always sees the latest
  // closure) when a package HANDLE label is tapped mid-session.
  const handleBoxTappedRef = useRef<(url: string) => void>(() => {});
  const onPackageHandle = useCallback(
    (url: string) => handleBoxTappedRef.current(url),
    [],
  );
  const session = useEncodeSession({ onPackageHandle });
  const {
    visibleRows,
    provisioned,
    packageTagCount,
    sessionActive,
    cancelling,
    sessionAddedCount,
    sessionAddedUids,
    runSession,
    stopSession,
    resetSession,
    seedPackageCount,
    cancelSession,
  } = session;

  // What the current session encodes to: a package (Packages flow) or Vera's UNASSIGNED pool
  // (Tags flow — company assignment is now a POST-scan step, not the encode target). Always has a
  // target, so the session is always armed.
  const runRef: EncodeSessionTargetRef | null = target
    ? { kind: "package", id: target.id }
    : isConfigureFlow
      ? { kind: "unassigned" }
      : null;

  const isPackage = !!target;
  const displayName = target?.name ?? null;
  const readerReady = helperReachable && isConnected;
  const targetCount = isPackage ? target?.targetCount : undefined;
  const bigCount =
    isPackage && packageTagCount != null ? packageTagCount : provisioned;
  const isPackageFilled =
    isPackage && targetCount != null && bigCount >= targetCount;

  // Company options (Tags flow only) — for the POST-scan "Assign to company" step.
  //
  // VRG-808 — `includeDemo` opts this ONE picker back in to demo companies. The repository
  // excludes `kind = 'demo'` from all seven pickers by default, and that default is right for
  // the other six; the reason it named for THIS one was "a demo company appearing in the
  // tag-encoding picker is how you ACCIDENTALLY encode a real chip against scraped-brand
  // assets" (company.drizzle.repository.ts). The operative word is accidentally — a chosen
  // "encode for this demo" is a different act, and it is the product's strongest sales motion:
  // mail a prospect a physical product whose tag opens their own branded certificate. Because
  // takeover is IN-PLACE (take-over-demo flips kind demo→standard on the same row), a tag
  // encoded here keeps working the moment they sign, with no re-encode.
  //
  // The accident is prevented by VISIBILITY instead of absence: a demo carries its Type badge in
  // the option row, exactly as it does on the Companies list, so it can never be mistaken for a
  // real tenant mid-scan.
  const { data: companiesData } = useCompaniesQuery(
    { limit: 200, includeDemo: true },
    { enabled: open && isConfigureFlow },
  );
  const companyOptions: CompanyOption[] = useMemo(
    () =>
      listedCompanies(companiesData).map((c) => ({
        label: c.name,
        value: c.id,
        image: c.logo,
        // Shared with the other two assignment pickers (§36); the limit it carries — demo and
        // internal only — is documented at `listedCompanyType`.
        type: listedCompanyType(c),
      })),
    [companiesData],
  );

  // Start the session for the current target. Package targets carry family + product identity;
  // company/unassigned targets have no product and default to NTAG 424 DNA.
  const runForTarget = useCallback(() => {
    if (!runRef) return;
    if (runRef.kind === "package" && target) {
      seedPackageCount(runRef.id);
      runSession({
        target: runRef,
        family: target.family,
        lock,
        writeProtect,
        productSku: target.productSku,
        productTitle: target.productTitle,
        productImage: target.productImage,
      });
    } else {
      runSession({ target: runRef, family: "ntag424_dna", lock, writeProtect });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    runRef?.kind,
    (runRef as any)?.id,
    target,
    lock,
    runSession,
    seedPackageCount,
  ]);

  // "Add to package": assign the tags encoded this session (company or unassigned) to a package.
  const [assigning, setAssigning] = useState(false);
  // Committing the deferred assignments on Done (the assign POSTs).
  const [committing, setCommitting] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const assigningRef = useRef(false);
  const showAddToPackage = !isPackage && provisioned > 0;
  // Post-scan sections (Tags flow) appear once there's something to assign.
  const showPostScan = isConfigureFlow && provisioned > 0;

  // Which of the session's tags are already assigned to a DIFFERENT company/package than the target
  // (a bulk assign on Done would MOVE them). Powers the confirm-to-move warning + row highlight.
  const checkConflicts = useCallback(
    async (
      kind: "company" | "package",
      targetId: string,
    ): Promise<{ uid: string; name: string }[]> => {
      const uids = sessionAddedUids;
      if (!uids.length) return [];
      try {
        const resp = await api.post("/packages/tags/assignment-conflicts", {
          uids,
          kind,
          targetId,
        });
        return resp?.data?.conflicts ?? [];
      } catch {
        return [];
      }
    },
    [sessionAddedUids],
  );

  // Post-scan "Assign to company": DEFERRED — record the pending company, then flag any tags already
  // in another company. Nothing is written until Done (so X leaves them encoded-but-unassigned).
  const handleAssignCompany = useCallback(
    async (opt: CompanyOption) => {
      setCompanyAssigned({ id: opt.value, name: opt.label, logo: opt.image });
      setCompanyConflicts(await checkConflicts("company", opt.value));
    },
    [checkConflicts],
  );

  // "Change" the pending company: clear it + its conflict flags (nothing was committed yet).
  const handleChangeCompany = useCallback(() => {
    setCompanyAssigned(null);
    setCompanyConflicts([]);
  }, []);

  // Teardown on close.
  useEffect(() => {
    if (open) return;
    stopSession();
    resetSession();
    setLock(false);
    setWriteProtect(false);
    setCompanyAssigned(null);
    setPackageAssigned(null);
    setCompanyConflicts([]);
    setPackageConflicts([]);
    setAssignError(null);
  }, [open, stopSession, resetSession]);

  // Auto-arm: (re)start whenever the active target's identity changes — the Packages flow arms on
  // open; the Tags flow arms on open (unassigned) and re-arms when a company is picked/cleared
  // (safe via the hook's run-generation guard). Keyed on identity only, so lock/other renders
  // don't restart.
  const targetKey = runRef
    ? runRef.kind === "package"
      ? `package:${runRef.id}`
      : runRef.kind === "company"
        ? `company:${runRef.id}`
        : "unassigned"
    : null;
  useEffect(() => {
    if (!open) return;
    // Switch target without a visible flash: DON'T stopSession() first (that flips sessionActive/
    // waiting to false and flickers the reader row). runForTarget() re-arms via the hook's run-
    // generation guard — it supersedes the old loop and keeps sessionActive true throughout.
    resetSession();
    if (runRef) runForTarget();
    else stopSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetKey]);

  const handleCancel = useCallback(async () => {
    if (!target || !isPackage) return;
    const ok = await cancelSession(target.id);
    if (ok) {
      refetch?.();
      onClose();
    }
  }, [target, isPackage, cancelSession, refetch, onClose]);

  // Resolve the tapped handle's package and RECORD it as the pending package — DEFERRED: the assign
  // POST happens on Done, not here. Tapping a box just reads which box (for the preview).
  const handleBoxTapped = useCallback(
    async (scannedUrl: string) => {
      if (assigningRef.current) return;
      const match = PACKAGE_HANDLE_URL_RE.exec(scannedUrl);
      if (!match) {
        setAssignError(
          "That tag isn't a package label — tap the box's own label.",
        );
        return;
      }
      assigningRef.current = true;
      setAssigning(true);
      setAssignError(null);
      try {
        // `api.get` resolves even on 4xx — check status explicitly.
        const resp = await api.get(`/packages/${match[1]}`);
        const pkg = resp?.data;
        const status = resp?.status;
        if (
          !pkg?.id ||
          (typeof status === "number" && (status < 200 || status >= 300))
        ) {
          setAssignError("That package label isn't recognized.");
          return;
        }
        // Record the pending package (with its product for the preview). Nothing is assigned until
        // Done; a "Change" button lets a different box be presented before then.
        setPackageAssigned(pkg);
        setPackageConflicts(await checkConflicts("package", pkg.id));
      } catch (error) {
        setAssignError(
          getErrorMessage(error) || "Could not read the package label",
        );
      } finally {
        assigningRef.current = false;
        setAssigning(false);
      }
    },
    [checkConflicts],
  );
  // Keep the ref current so the encode loop always calls the latest closure.
  handleBoxTappedRef.current = handleBoxTapped;

  // "Change" the pending package: clear it + its conflict flags (nothing was committed yet).
  const handleChangePackage = useCallback(() => {
    setPackageAssigned(null);
    setPackageConflicts([]);
    setAssignError(null);
  }, []);

  // The assigned package's PRODUCT, shaped for ProductPreview (product image + title), with a
  // "Qty N · SKU" sublabel — the identity that matters, not the raw package handle name.
  const packagePreview = useMemo(() => {
    if (!packageAssigned) return null;
    const p = packageAssigned.tag_shop_product;
    const sv = p?.selected_variant;
    const title = p?.title ?? packageAssigned.name;
    const image = sv?.image ?? p?.images?.[0] ?? null;
    const sku = sv?.sku ?? p?.sku ?? null;
    // selected_variant.quantity is often null; the variant title IS the qty tier ("250"), and
    // variants[] may carry a numeric quantity — prefer a real number, else the tier label.
    const quantity =
      p?.variants?.find((v: any) => v?.id === sv?.id)?.quantity ??
      sv?.quantity ??
      sv?.title ??
      null;
    return { title, image, sku, quantity };
  }, [packageAssigned]);

  // Uids that would be MOVED by the pending assignment (already in another package/company) — the
  // session-body highlights these rows so it's clear which tags the "move" warning refers to.
  const conflictUids = useMemo(
    () => new Set([...companyConflicts, ...packageConflicts].map((c) => c.uid)),
    [companyConflicts, packageConflicts],
  );

  // Done — the point assignment actually happens (DEFERRED from pick/tap). Commit the pending
  // company/package assignments for the Tags flow, then close with the confirmation toast. Because
  // this is the only place the assign POSTs fire, closing with X leaves the tags encoded-but-
  // unassigned.
  const handleDone = useCallback(async () => {
    const uids = sessionAddedUids;
    if (!isPackage && uids.length) {
      setCommitting(true);
      try {
        // reassign:true so tags already in another package/company are MOVED here (the user has been
        // shown the confirm-to-move warning at pick/tap time); harmless for non-conflicting tags.
        if (packageAssigned) {
          await api.post(`/packages/${packageAssigned.id}/tags/assign`, {
            uids,
            reassign: true,
          });
        }
        if (companyAssigned) {
          await api.post("/packages/tags/assign-company", {
            companyId: companyAssigned.id,
            uids,
            reassign: true,
          });
        }
      } catch (error) {
        notification({
          type: "error",
          title: getErrorMessage(error) || "Could not complete the assignment",
        });
        setCommitting(false);
        return; // keep the modal open so it can be retried/changed
      }
      setCommitting(false);
    }
    if (provisioned > 0) {
      let where: string;
      if (isPackage) {
        where = `added to ${target?.name}`;
      } else {
        // Tags flow — a session can be BOTH added to a package AND assigned to a company, so
        // combine them ("added to X and assigned to Y") rather than reporting only one.
        const parts: string[] = [];
        if (packageAssigned) parts.push(`added to ${packageAssigned.name}`);
        if (companyAssigned) parts.push(`assigned to ${companyAssigned.name}`);
        where = parts.length
          ? parts.join(" and ")
          : "encoded and added to Vera";
      }
      notification({
        type: "success",
        title: `${provisioned} tag${provisioned === 1 ? "" : "s"} ${where}`,
      });
    }
    refetch?.();
    onClose();
  }, [
    sessionAddedUids,
    isPackage,
    provisioned,
    target,
    packageAssigned,
    companyAssigned,
    notification,
    refetch,
    onClose,
  ]);

  // X / backdrop — close WITHOUT committing the pending company/package assignment. The tags stay
  // encoded and in Vera (unassigned); only Done commits. So the toast never falsely claims an
  // assignment that didn't happen.
  const handleClose = useCallback(() => {
    if (cancelling || assigning || committing) return;
    if (provisioned > 0) {
      const where = isPackage
        ? `added to ${target?.name}`
        : "encoded and added to Vera";
      notification({
        type: "success",
        title: `${provisioned} tag${provisioned === 1 ? "" : "s"} ${where}`,
      });
    }
    refetch?.();
    onClose();
  }, [
    cancelling,
    assigning,
    committing,
    provisioned,
    isPackage,
    target,
    notification,
    refetch,
    onClose,
  ]);

  return (
    <Modal
      title={displayName ? `Add tags — ${displayName}` : "Add tags"}
      open={open}
      onClose={handleClose}
      actions={
        <>
          {/* Resume — only when a session paused mid-fill (reader disconnect).
              §27: stays visible + disabled with a tooltip naming the missing
              permission — internal-only action, so the ask is "a Vera admin",
              not "an admin" (the company-scoped phrasing used elsewhere). */}
          {runRef && !sessionActive && visibleRows.length > 0 && (
            <Tooltip
              title={
                canEncode
                  ? ""
                  : "You need the 'Upload packages' permission. Ask a Vera admin."
              }
              arrow
            >
              <span style={{ display: "inline-flex" }}>
                <Button
                  variant="secondary"
                  disabled={!readerReady || !canEncode}
                  onClick={runForTarget}
                >
                  Resume
                </Button>
              </span>
            </Tooltip>
          )}
          {/* Cancel — package sessions only: un-assign this session's freshly-provisioned tags. */}
          {isPackage && (
            <Button
              variant="secondary"
              disabled={cancelling || sessionAddedCount === 0}
              loading={cancelling}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
          <Button variant="primary" onClick={handleDone} loading={committing}>
            Done
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {/* Session — always armed (Packages flow: the box; Tags flow: the unassigned pool). */}
        <EncodeSessionBody
          session={session}
          helperReachable={helperReachable}
          isConnected={isConnected}
          readerName={readerName}
          lock={lock}
          setLock={setLock}
          writeProtect={writeProtect}
          setWriteProtect={setWriteProtect}
          // VRG-252 — drives the lock warning's wording. A package target carries the variant's
          // family; the unassigned/Tags flow defaults to the 424, matching `runForTarget` below.
          family={target?.family ?? "ntag424_dna"}
          bigCount={bigCount}
          targetCount={targetCount}
          isPackageFilled={isPackageFilled}
          conflictUids={conflictUids}
          counterLabel={
            isPackage
              ? "in this package"
              : companyAssigned
                ? "encoded"
                : "in Vera (unassigned)"
          }
        />

        {/* Post-scan sections (Tags flow). Wrapped in Collapse so the modal height ANIMATES
            smoothly (and the centered modal recenters smoothly) instead of jumping when they
            appear — both gate on provisioned>0 together. */}
        <Collapse in={showPostScan} unmountOnExit>
          <Stack spacing={4}>
            <Box
              sx={{
                border: `1px solid ${theme.palette.primary[100]}`,
                borderRadius: 2,
                px: 4,
                py: 4,
              }}
            >
              <Stack spacing={3}>
                <Stack spacing={0.5}>
                  <Typography variant="body3">
                    Assign to company{" "}
                    <Typography
                      component="span"
                      variant="body3"
                      color="text.secondary"
                    >
                      (optional)
                    </Typography>
                  </Typography>
                </Stack>

                {companyAssigned ? (
                  <Stack spacing={3}>
                    <Preview
                      item={companyAssigned}
                      onChange={handleChangeCompany}
                      disabled={assigningCompany}
                    >
                      <CompanyAvatar
                        name={companyAssigned.name}
                        logo={companyAssigned.logo}
                        size={48}
                      />
                      <Typography variant="body2" noWrap>
                        {companyAssigned.name}
                      </Typography>
                    </Preview>
                    {companyConflicts.length > 0 && (
                      <Alert
                        severity="warning"
                        description={`${companyConflicts.length} of these tag${
                          companyConflicts.length === 1 ? " is" : "s are"
                        } already assigned to another company. Done will move ${
                          companyConflicts.length === 1 ? "it" : "them"
                        } here.`}
                      />
                    )}
                  </Stack>
                ) : (
                  <Autocomplete
                    fullWidth
                    hideStartIcon
                    autoHighlight
                    options={companyOptions}
                    value={null}
                    onSelect={(opt: CompanyOption | null) =>
                      opt && handleAssignCompany(opt)
                    }
                    placeholder="Search companies"
                    getOptionLabel={(opt: CompanyOption) => opt?.label ?? ""}
                    isOptionEqualToValue={(
                      a: CompanyOption,
                      b: CompanyOption,
                    ) => a?.value === b?.value}
                    renderOption={(opt: CompanyOption) => (
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        sx={{ py: 0.5, minWidth: 0, width: "100%" }}
                      >
                        <CompanyAvatar
                          logo={opt.image}
                          name={opt.label}
                          size={40}
                        />
                        <Typography variant="body2" noWrap>
                          {opt.label}
                        </Typography>
                        {opt.type && (
                          <StatusChip
                            statuses={COMPANY_SEGMENT_STATUSES}
                            status={opt.type}
                            size="small"
                          />
                        )}
                      </Stack>
                    )}
                  />
                )}

                {assigningCompany && (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <CircularProgress
                      size={18}
                      thickness={4}
                      sx={{ color: theme.palette.brand[500] }}
                    />
                    <Typography variant="body3" color="text.secondary">
                      Assigning…
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
            {/* Add to package — tap the box's own handle label to assign the encoded tags to it. */}
            <Box
              sx={{
                border: `1px solid ${theme.palette.primary[100]}`,
                borderRadius: 2,
                px: 4,
                py: 4,
              }}
            >
              <Stack spacing={3}>
                <Stack spacing={0.5}>
                  <Typography variant="body3">
                    Add to package{" "}
                    <Typography
                      component="span"
                      variant="body3"
                      color="text.secondary"
                    >
                      (optional)
                    </Typography>
                  </Typography>
                  {!packageAssigned && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5 }}
                    >
                      Tap the box's own label on the reader to assign the{" "}
                      {provisioned} tag
                      {provisioned === 1 ? "" : "s"} you just encoded to it. Or
                      click Done to leave them in the unassigned pool.
                    </Typography>
                  )}
                </Stack>

                {packageAssigned ? (
                  <Stack spacing={3}>
                    <PackagePreview
                      title={packagePreview?.title}
                      image={packagePreview?.image}
                      quantity={packagePreview?.quantity}
                      sku={packagePreview?.sku}
                      onChange={handleChangePackage}
                      disabled={assigning}
                    />
                    {packageConflicts.length > 0 && (
                      <Alert
                        severity="warning"
                        description={`${packageConflicts.length} of these tag${
                          packageConflicts.length === 1 ? " is" : "s are"
                        } already in another package. Done will move ${
                          packageConflicts.length === 1 ? "it" : "them"
                        } here.`}
                      />
                    )}
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={2} alignItems="center">
                    {assigning ? (
                      <CircularProgress
                        size={18}
                        thickness={4}
                        sx={{ color: theme.palette.brand[500] }}
                      />
                    ) : (
                      <RadioIcon
                        strokeWidth={1.5}
                        color={theme.palette.primary.main}
                      />
                    )}
                    <Typography variant="body3" color="text.secondary">
                      {assigning
                        ? "Assigning…"
                        : "Present the box's label on the reader…"}
                    </Typography>
                  </Stack>
                )}

                {assignError && (
                  <Alert severity="warning" description={assignError} />
                )}
              </Stack>
            </Box>
          </Stack>
        </Collapse>
      </Stack>
    </Modal>
  );
};
